import { createFileRoute } from '@tanstack/react-router'
import {
  CalendarX2,
  CircleCheck,
  FileX2,
  Link2Off,
  Mail,
  Phone,
  RefreshCw,
  RotateCw,
  WifiOff,
  XCircle,
} from 'lucide-react'
import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { Logo } from '@/components/brand/logo'
import { AcceptForm, DeclineForm, type AcceptValues } from '@/components/proposal/accept-form'
import { getJson, PublicError } from '@/components/proposal/client-api'
import { formatDate } from '@/components/proposal/format'
import { OnboardingForm } from '@/components/proposal/onboarding-form'
import { ProposalPresentation } from '@/components/proposal/presentation'
import { AcceptedReceipt, ClientSignature } from '@/components/proposal/receipt'
import type { PublicProposal, Snapshot } from '@/components/proposal/types'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'

/** The client's proposal page. Public: access is the link itself. */
export const Route = createFileRoute('/p/$token')({ component: ProposalPage })

type Load =
  | { status: 'loading' }
  | { status: 'ready'; data: PublicProposal }
  | { status: 'invalid' }
  | { status: 'error'; message: string }

function ProposalPage() {
  const { token } = Route.useParams()
  const [load, setLoad] = useState<Load>({ status: 'loading' })
  const [values, setValues] = useState<AcceptValues | null>(null)

  useLightDocument()

  const fetchProposal = useCallback(async () => {
    try {
      const data = await getJson<PublicProposal>(`/api/public/p/${encodeURIComponent(token)}`)
      setLoad({ status: 'ready', data })
    } catch (err) {
      if (err instanceof PublicError && err.status === 404) setLoad({ status: 'invalid' })
      else
        setLoad({
          status: 'error',
          message: err instanceof Error ? err.message : 'Something went wrong.',
        })
    }
  }, [token])

  useEffect(() => {
    void fetchProposal()
  }, [fetchProposal])

  useEffect(() => {
    document.title =
      load.status === 'ready'
        ? `${load.data.snapshot.title} · Proposal from HCD Cleaning`
        : 'Proposal from HCD Cleaning'
  }, [load])

  const onValues = useCallback((v: AcceptValues) => setValues(v), [])

  if (load.status === 'loading') return <CoverSkeleton />
  if (load.status === 'invalid') {
    return (
      <Notice icon={<Link2Off />} title="This link is not valid">
        Please check you have the full link from your email, or ask HCD Cleaning to send you a new
        one.
      </Notice>
    )
  }
  if (load.status === 'error') {
    return (
      <Notice icon={<WifiOff />} title="We could not load your proposal">
        <p>{load.message}</p>
        <Button className="mt-5 h-11" onClick={() => void fetchProposal()}>
          <RotateCw />
          Try again
        </Button>
      </Notice>
    )
  }

  const { data } = load
  const s = data.snapshot
  const a = data.acceptance
  const agreementValues = values
    ? {
        client_name: values.signerName,
        client_address: values.registeredAddress,
        client_business: values.businessDescription,
      }
    : undefined

  return (
    <ProposalPresentation
      snapshot={s}
      mode="client"
      token={token}
      state={data.state}
      banner={<StateBanner state={data.state} s={s} />}
      agreementValues={agreementValues}
      agreementText={a?.agreementText || undefined}
      clientPanel={
        a ? (
          <ClientSignature a={a} />
        ) : data.state === 'open' ? (
          <div className="grid gap-6">
            <AcceptForm
              snapshot={s}
              token={token}
              contentHash={data.contentHash}
              customerKind={data.customerKind}
              onValues={onValues}
              onAccepted={() => void fetchProposal()}
            />
            <DeclineForm token={token} onDeclined={() => void fetchProposal()} />
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">This proposal can no longer be signed.</p>
        )
      }
    >
      {a && (
        <div className="grid gap-8 pt-14 print:pt-6">
          <AcceptedReceipt s={s} a={a} />
          {data.onboarding?.completed ? (
            <AllSet />
          ) : data.prefill ? (
            <div className="print:hidden">
              <OnboardingForm
                token={token}
                prefill={data.prefill}
                customerKind={data.customerKind}
                onDone={() => void fetchProposal()}
              />
            </div>
          ) : null}
        </div>
      )}
    </ProposalPresentation>
  )
}

function StateBanner({ state, s }: { state: PublicProposal['state']; s: Snapshot }) {
  const contact = (
    <span className="mt-3 flex flex-wrap gap-2">
      {s.company.phone && (
        <a
          href={`tel:${s.company.phone.replace(/\s/g, '')}`}
          className="inline-flex h-10 items-center gap-2 rounded-md border bg-card px-3 text-sm font-medium hover:bg-secondary"
        >
          <Phone className="size-4" aria-hidden />
          {s.company.phone}
        </a>
      )}
      {s.company.email && (
        <a
          href={`mailto:${s.company.email}?subject=${encodeURIComponent(`Proposal ${s.reference}`)}`}
          className="inline-flex h-10 items-center gap-2 rounded-md border bg-card px-3 text-sm font-medium hover:bg-secondary"
        >
          <Mail className="size-4" aria-hidden />
          {s.company.email}
        </a>
      )}
    </span>
  )
  const map: Partial<
    Record<
      PublicProposal['state'],
      { icon: ReactNode; title: string; body: ReactNode; tone: 'info' | 'warn' | 'good' }
    >
  > = {
    expired: {
      icon: <CalendarX2 />,
      tone: 'warn',
      title: `This proposal expired on ${formatDate(s.validUntil)}.`,
      body: <>Contact HCD Cleaning for an updated one.{contact}</>,
    },
    withdrawn: {
      icon: <FileX2 />,
      tone: 'warn',
      title: 'This proposal has been withdrawn.',
      body: (
        <>
          HCD Cleaning has withdrawn it. If you were expecting to sign, please get in touch.
          {contact}
        </>
      ),
    },
    superseded: {
      icon: <RefreshCw />,
      tone: 'info',
      title: 'A newer version has been sent to you.',
      body: (
        <>
          Please use the latest link from HCD Cleaning. This version can no longer be signed.
          {contact}
        </>
      ),
    },
    declined: {
      icon: <XCircle />,
      tone: 'info',
      title: 'You declined this proposal.',
      body: (
        <>Thank you for letting us know. If anything changes, we would be glad to help.{contact}</>
      ),
    },
    accepted: {
      icon: <CircleCheck />,
      tone: 'good',
      title: 'Signed and accepted.',
      body: <>Your signed copy and next steps are at the end of this page.</>,
    },
  }
  const m = map[state]
  if (!m) return null
  const tone =
    m.tone === 'warn'
      ? 'border-warning/50 bg-warning/10 [&>span:first-child]:text-warning'
      : m.tone === 'good'
        ? 'border-success/40 bg-success/10 [&>span:first-child]:text-success'
        : 'border-primary/30 bg-accent/60 [&>span:first-child]:text-primary'
  return (
    <div role="status" className={`flex items-start gap-3 rounded-xl border p-4 ${tone}`}>
      <span className="mt-0.5 shrink-0 [&_svg]:size-5" aria-hidden>
        {m.icon}
      </span>
      <div>
        <p className="font-semibold">{m.title}</p>
        <div className="text-[15px] text-foreground/85">{m.body}</div>
      </div>
    </div>
  )
}

function AllSet() {
  return (
    <section
      aria-labelledby="all-set"
      className="flex items-start gap-4 rounded-2xl bg-ink p-6 text-white sm:p-8"
    >
      <CircleCheck className="size-8 shrink-0 text-rinse" aria-hidden />
      <div>
        <h2 id="all-set" className="text-2xl font-semibold" style={{ fontStretch: '86%' }}>
          All set.
        </h2>
        <p className="mt-1 text-white/80">We'll be in touch to arrange your start date.</p>
      </div>
    </section>
  )
}

function Notice({
  icon,
  title,
  children,
}: {
  icon: ReactNode
  title: string
  children: ReactNode
}) {
  return (
    <main className="grid min-h-dvh place-items-center bg-background px-5 py-10">
      <div className="max-w-md text-center">
        <Logo className="mx-auto mb-8 h-12" />
        <span
          className="mx-auto flex size-12 items-center justify-center rounded-full bg-accent text-accent-foreground [&_svg]:size-6"
          aria-hidden
        >
          {icon}
        </span>
        <h1 className="mt-4 text-2xl font-semibold">{title}</h1>
        <div className="mt-2 text-[15px] text-muted-foreground">{children}</div>
      </div>
    </main>
  )
}

function CoverSkeleton() {
  return (
    <div className="min-h-dvh bg-ink" aria-busy="true" aria-label="Loading your proposal">
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 pt-8 sm:px-6">
        <Skeleton className="h-12 w-36 bg-white/15" />
        <div className="mt-24 grid gap-4">
          <Skeleton className="h-5 w-24 bg-white/15" />
          <Skeleton className="h-20 w-4/5 bg-white/15" />
          <Skeleton className="h-6 w-2/3 bg-white/10" />
          <Skeleton className="h-6 w-1/2 bg-white/10" />
        </div>
      </div>
    </div>
  )
}

/** Client documents always render in the light theme, whatever the device prefers. */
function useLightDocument() {
  useEffect(() => {
    const root = document.documentElement
    const force = () => {
      if (root.classList.contains('dark')) root.classList.remove('dark')
      root.style.colorScheme = 'light'
    }
    force()
    const obs = new MutationObserver(force)
    obs.observe(root, { attributes: true, attributeFilter: ['class'] })
    return () => obs.disconnect()
  }, [])
}
